<template>
  <div class="windows-container">
    <Window
      v-for="(window, index) in windows"
      :key="index"
      :window-datum="window"
      @minimize="minimize(window)"
      @active="active(window)"
      @close="closeWindow(window.id, $event)"
      @setReturnData="setReturnData(window.id, $event)"
      v-show="!window.mini"
      :style="{ 'z-index': window.zIndex }"
    ></Window>
    <div class="mask" v-show="isMask" :style="{ zIndex: maxZIndex - 1 }"></div>
    <div class="task-bar">
      <div class="task" v-for="(id, index) in miniWindows" :key="index">
        <div class="title" @click="normalize(windows[id])">
          {{ windows[id].title }}
        </div>
        <div class="btn-group">
          <sc-icon class="btn" type="CloseOutlined" @click="closeWindow(id)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Window from './Window.vue';
import { WindowData, WindowDatum, WindowDatumInput } from './WindowInterface';
import mitt from 'mitt';

export default defineComponent({
  name: 'ScWindows',
  components: { Window },
  setup() {
    return {
      emitter: mitt(),
    };
  },
  data() {
    return {
      maxZIndex: 1000,
      windows: {} as WindowData,
      miniWindows: [] as number[],
      offset: {
        x: 0,
        y: 0,
        dx: 1,
        dy: 1,
      },
      id: 0,
    };
  },
  computed: {
    isMask(): boolean {
      let value = false;
      const windows = Object.values(this.windows);
      if (windows.length > 0) {
        value = windows.reduce((previousValue, currentValue) =>
          previousValue.zIndex > currentValue.zIndex
            ? previousValue
            : currentValue
        ).mask;
      }
      return value;
    },
  },
  methods: {
    open(windowDatumInput: WindowDatumInput) {
      const window = this.initWindow(windowDatumInput);
      this.windows[window.id] = window;
      this.updateOffset();
      return window.id;
    },

    close(id: number) {
      this.closeWindow(id);
    },

    getCloseData(id: number) {
      return new Promise((resolve) => {
        const listener = (ret: any) => {
          this.emitter.off('close_' + id, listener);
          resolve(ret);
        };
        this.emitter.on('close_' + id, listener);
      });
    },

    /**
     * 初始化窗口参数
     * @param windowDatumInput
     */
    initWindow(windowDatumInput: WindowDatumInput): WindowDatum {
      const id = ++this.id;
      const content = windowDatumInput.content;
      const title = windowDatumInput.title || '';
      const mask = windowDatumInput.mask || false;
      const width = windowDatumInput.width || 1000;
      const height = Math.min(windowDatumInput.height || 800, document.body.clientHeight - 120);
      const padding = windowDatumInput.padding || 0;
      const left =
        windowDatumInput.left ||
        Math.max(
          0,
          (document.body.clientWidth -
            Math.min(document.body.clientWidth - 120, width)) /
            2 +
            this.offset.x
        );
      const top =
        windowDatumInput.top ||
        (document.body.clientHeight -
          Math.min(document.body.clientHeight - 120, height)) /
          2 +
          this.offset.y;
      const zIndex = this.getNextZIndex();
      return {
        id,
        content,
        title,
        width,
        height,
        padding,
        left,
        top,
        zIndex,
        mini: false,
        mask,
      };
    },

    getNextZIndex() {
      this.maxZIndex += 2;
      return this.maxZIndex;
    },

    /**
     * 更新初始位置，防止窗口重叠
     */
    updateOffset() {
      this.offset.y += 12 * this.offset.dy;
      this.offset.x += 12 * this.offset.dx;
      if (this.offset.y > 12 || this.offset.y < -12) {
        this.offset.dy = -this.offset.dy;
      }
      if (this.offset.x > 100 || this.offset.x < -100) {
        this.offset.dx = -this.offset.dx;
      }
    },

    active(window: WindowDatum) {
      if (window.zIndex < this.maxZIndex) {
        window.zIndex = this.getNextZIndex();
      }
    },

    minimize(window: WindowDatum) {
      window.mini = true;
      this.miniWindows.push(window.id);
    },

    deleteIdInMiniWindows(id: number) {
      for (const index in this.miniWindows) {
        if (this.miniWindows[index] === id) {
          this.miniWindows.splice(Number(index), 1);
          break;
        }
      }
    },
    normalize(window: WindowDatum) {
      window.mini = false;
      this.deleteIdInMiniWindows(window.id);
    },
    setReturnData(id: number, ret?: any) {
      this.windows[id].retData = ret;
    },
    closeWindow(id: number, ret?: any) {
      this.deleteIdInMiniWindows(id);
      const retData = ret !== undefined ? ret : this.windows[id].retData;
      delete this.windows[id];
      this.emitter.emit('close_' + id, retData);
    },
  },
});
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  opacity: 0.7;
  background-color: #363636;
}

.windows-container {
  $background-color: #e0e0e0;
  $title-height: 38px;

  position: absolute;
  z-index: 10;
  top: 0;
  left: 0;

  .task-bar {
    height: $title-height;
    position: fixed;
    left: 0;
    bottom: 0;
    max-width: 100vw;
    z-index: $index-middle;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;

    > .task {
      position: relative;
      margin-right: 2px;
      user-select: none;
      display: flex;
      flex-direction: row;
      padding: 0 $spacing-medium;
      color: $color-content-text;
      background-color: $color-content-bg-secondary;
      border-radius: $border-radius-medium $border-radius-medium 0 0;

      > .title {
        height: $title-height;
        line-height: $title-height;
        max-width: 120px;
        min-width: 30px;
        overflow: hidden;
        word-break: break-word;
        font-size: $font-size;
        cursor: pointer;
      }

      > .btn-group {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        > .btn {
          @include flexRowCenter();
          position: relative;
          width: 16px;
          height: 16px;
          margin-left: 6px;
          font-size: $font-size-extra-small;
          font-weight: $font-weight-primary;
          cursor: pointer;
          border-radius: $border-radius-circle;

          &:hover {
            background-color: darken($background-color, 2);
          }
        }
      }
    }
  }
}
</style>
